import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star } from 'lucide-react';
import { format } from 'date-fns';

interface Answer {
  question_id: string;
  answer_text: string | null;
  answer_rating: number | null;
  answer_choice: string | null;
}

interface Response {
  id: string;
  completed_at: string;
  answers: Answer[];
}

interface Question {
  id: string;
  question_text: string;
  question_type: 'text' | 'rating' | 'multiple_choice';
  sequence_order: number;
}

interface SurveyComparisonViewProps {
  questions: Question[];
  responses: Response[];
  onClose: () => void;
}

function StarDisplay({ rating, max = 5 }: { rating: number; max?: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Star
          key={i}
          className={
            i < rating
              ? 'h-4 w-4 fill-primary text-primary'
              : 'h-4 w-4 text-muted-foreground'
          }
        />
      ))}
    </div>
  );
}

export function SurveyComparisonView({ questions, responses, onClose }: SurveyComparisonViewProps) {
  const sorted = [...questions].sort((a, b) => a.sequence_order - b.sequence_order);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Your Progress</h3>
        <p className="text-sm text-muted-foreground">
          See how your answers have changed over time.
        </p>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium align-bottom min-w-[200px]">
                Question
              </th>
              {responses.map((r, i) => {
                const isLatest = i === responses.length - 1;
                return (
                  <th
                    key={r.id}
                    className={
                      'text-left p-3 font-medium align-bottom min-w-[160px]' +
                      (isLatest ? ' bg-primary/5' : '')
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span>{format(new Date(r.completed_at), 'MMM d, yyyy')}</span>
                      {isLatest && (
                        <Badge variant="default" className="text-[10px]">
                          Latest
                        </Badge>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((q) => (
              <tr key={q.id} className="border-t">
                <td className="p-3 align-top font-medium">{q.question_text}</td>
                {responses.map((r, i) => {
                  const isLatest = i === responses.length - 1;
                  const answer = r.answers.find((a) => a.question_id === q.id);
                  return (
                    <td
                      key={r.id}
                      className={
                        'p-3 align-top' + (isLatest ? ' bg-primary/5' : '')
                      }
                    >
                      {!answer ? (
                        <span className="text-muted-foreground">—</span>
                      ) : q.question_type === 'rating' &&
                        answer.answer_rating !== null ? (
                        <StarDisplay rating={answer.answer_rating} />
                      ) : (
                        <span>
                          {answer.answer_text ||
                            answer.answer_choice ||
                            '—'}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}
